import { createFileRoute } from "@tanstack/react-router";
import {
  Briefcase,
  ExternalLink,
  MapPin,
  ArrowUpRight,
  Sparkles,
  CheckCircle2,
  Users,
  Building2,
  HeartHandshake,
  MessageCircle,
} from "lucide-react";
import { useBrand, useSiteSection, waLink } from "@/hooks/useSiteContent";

export const Route = createFileRoute("/trabalhe-conosco")({
  head: () => ({
    meta: [
      { title: "Trabalhe Conosco — Vagas na Contato Internet" },
      {
        name: "description",
        content:
          "Faça parte do time da Contato Internet. Confira as vagas abertas e envie seu currículo para trabalhar com tecnologia no Sul de SC.",
      },
      { property: "og:title", content: "Trabalhe Conosco — Contato Internet" },
      { property: "og:description", content: "Vagas abertas e cadastro de currículo." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: JobsPage,
});

const DEFAULT_CADASTRO_URL =
  "https://engrenagemrh.empregare.com/pt-br/cadastrar-curriculo";

function JobsPage() {
  const jobs = useSiteSection("jobs");
  const brand = useBrand();

  const cadastroUrl = jobs.talentCtaHref?.trim() || DEFAULT_CADASTRO_URL;
  const openings = (jobs.items ?? []).filter((j) => j.role);

  const perks = [
    {
      icon: Users,
      title: "Time colaborativo",
      description:
        "Ambiente dinâmico com pessoas apaixonadas por conectar vidas e negócios.",
    },
    {
      icon: Building2,
      title: "Estrutura moderna",
      description:
        "Infraestrutura própria de ponta e tecnologia presente em mais de 30 municípios.",
    },
    {
      icon: HeartHandshake,
      title: "Crescimento contínuo",
      description:
        "Oportunidades de aprendizado prático e desenvolvimento na sua carreira.",
    },
  ];

  function openWhatsAppRH() {
    const text =
      "Olá! Gostaria de informações sobre vagas e oportunidades de trabalho na Contato.";
    window.open(waLink(brand.whatsapp, text, "trabalhe conosco"), "_blank", "noreferrer");
  }

  return (
    <>
      {/* Hero Header */}
      <section className="relative overflow-hidden bg-[linear-gradient(110deg,var(--brand-sky-deep),var(--brand-deep))] py-14 text-brand-deep-foreground sm:py-20">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-24 -top-24 size-96 rounded-full bg-brand-sky/15 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-24 left-1/3 size-80 rounded-full bg-brand-yellow/10 blur-3xl"
        />

        <div className="relative mx-auto max-w-7xl px-5">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3.5 py-1 text-[0.7rem] font-extrabold uppercase tracking-[0.2em] text-brand-yellow backdrop-blur-md">
            <Sparkles className="size-3.5" />
            Carreiras & Oportunidades
          </div>
          <h1 className="mt-4 max-w-3xl text-3xl font-extrabold uppercase leading-tight tracking-tight sm:text-4xl md:text-5xl">
            {jobs.title || "Trabalhe conosco"}
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-brand-sky sm:text-base md:text-lg">
            {jobs.subtitle ||
              "Venha fazer parte de um time catarinense que conecta mais de 30 municípios do Sul de Santa Catarina."}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a
              href={cadastroUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2.5 rounded-full bg-brand-lime px-7 py-3.5 text-xs font-extrabold uppercase tracking-wider text-brand-deep shadow-lg shadow-brand-lime/20 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:brightness-105"
            >
              <span>{jobs.talentCtaLabel || "Cadastrar currículo"}</span>
              <ArrowUpRight className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>

            <button
              type="button"
              onClick={openWhatsAppRH}
              className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-6 py-3.5 text-xs font-extrabold uppercase tracking-wider text-white backdrop-blur-sm transition-all hover:bg-white/15"
            >
              <MessageCircle className="size-4 text-brand-lime" />
              <span>Dúvidas com RH</span>
            </button>
          </div>
        </div>
      </section>

      {/* Conteúdo Principal */}
      <section className="bg-background py-14 sm:py-20">
        <div className="mx-auto max-w-7xl px-5">
          {/* Grid: Vagas Abertas + Card Hero de Redirecionamento Engrenagem RH */}
          <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr] lg:items-start">
            {/* Coluna da Esquerda: Lista de Vagas */}
            <div>
              <div className="flex items-end justify-between">
                <div>
                  <span className="text-[0.7rem] font-extrabold uppercase tracking-widest text-primary">
                    Oportunidades
                  </span>
                  <h2 className="mt-1 text-2xl font-extrabold uppercase tracking-tight text-brand-deep sm:text-3xl">
                    Vagas abertas
                  </h2>
                </div>
                {openings.length > 0 && (
                  <span className="rounded-full bg-secondary px-3 py-1 text-xs font-bold text-primary">
                    {openings.length} {openings.length === 1 ? "vaga" : "vagas"}
                  </span>
                )}
              </div>

              <ul className="mt-6 space-y-4">
                {openings.length === 0 ? (
                  <li className="rounded-3xl border border-dashed border-border bg-card/50 p-8 text-center">
                    <Briefcase className="mx-auto size-10 text-muted-foreground/60" />
                    <p className="mt-3 text-sm font-semibold text-foreground">
                      Nenhuma vaga específica listada no momento
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Cadastre seu currículo no nosso portal oficial para que possamos entrar em contato quando surgir uma oportunidade.
                    </p>
                    <a
                      href={cadastroUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-xs font-extrabold uppercase tracking-wider text-primary-foreground hover:opacity-95"
                    >
                      Cadastrar no banco de talentos
                      <ExternalLink className="size-3.5" />
                    </a>
                  </li>
                ) : (
                  openings.map((job, i) => (
                    <li
                      key={`${job.role}-${job.location}-${i}`}
                      className="group rounded-3xl border border-border bg-card p-6 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md"
                    >
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-start gap-4">
                          <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-secondary text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                            <Briefcase className="size-5" strokeWidth={2.4} />
                          </span>
                          <div className="min-w-0">
                            <h3 className="text-base font-extrabold text-brand-deep group-hover:text-primary sm:text-lg">
                              {job.role}
                            </h3>
                            <p className="mt-1 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-brand-sky">
                              <MapPin className="size-3.5 shrink-0" />
                              {job.location}
                            </p>
                          </div>
                        </div>

                        <div>
                          <a
                            href={job.url?.trim() ? job.url : cadastroUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-secondary px-5 py-2.5 text-xs font-extrabold uppercase tracking-wide text-primary transition-all hover:bg-primary hover:text-primary-foreground sm:w-auto"
                          >
                            <span>Candidatar-se</span>
                            <ArrowUpRight className="size-3.5" />
                          </a>
                        </div>
                      </div>
                    </li>
                  ))
                )}
              </ul>
            </div>

            {/* Coluna da Direita: Card de Redirecionamento Moderno */}
            <div className="sticky top-24">
              <div className="relative overflow-hidden rounded-[2rem] border border-border/80 bg-gradient-to-br from-card via-card to-secondary/30 p-7 shadow-xl sm:p-9">
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -right-16 -top-16 size-48 rounded-full bg-brand-lime/10 blur-2xl"
                />

                <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-lime/20 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-brand-deep">
                  <Sparkles className="size-3 text-brand-deep" />
                  Portal Oficial de Talentos
                </span>

                <h3 className="mt-4 text-xl font-extrabold uppercase leading-snug text-brand-deep sm:text-2xl">
                  {jobs.talentTitle || "Banco de Talentos"}
                </h3>

                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {jobs.talentText ||
                    "Cadastre seu currículo na nossa plataforma oficial de recrutamento para que nosso time de Gente & Gestão encontre você quando surgir uma vaga compatível com seu perfil."}
                </p>

                <div className="mt-6 space-y-3 rounded-2xl border border-border/60 bg-background/80 p-4">
                  <div className="flex items-start gap-2.5 text-xs text-foreground/90">
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-600" />
                    <span>Processo 100% online, rápido e estruturado</span>
                  </div>
                  <div className="flex items-start gap-2.5 text-xs text-foreground/90">
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-600" />
                    <span>Seus dados protegidos e analisados direto pelo RH</span>
                  </div>
                  <div className="flex items-start gap-2.5 text-xs text-foreground/90">
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-600" />
                    <span>Acesso a todas as oportunidades abertas na Contato</span>
                  </div>
                </div>

                <div className="mt-8 space-y-3">
                  <a
                    href={cadastroUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex w-full items-center justify-center gap-2.5 rounded-full bg-brand-lime px-6 py-4 text-xs font-extrabold uppercase tracking-wider text-brand-deep shadow-lg shadow-brand-lime/25 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:brightness-105"
                  >
                    <span>{jobs.talentCtaLabel || "Cadastrar currículo agora"}</span>
                    <ExternalLink className="size-4 transition-transform group-hover:scale-110" />
                  </a>

                  <p className="text-center text-[11px] text-muted-foreground">
                    Você será direcionado ao portal seguro da Engrenagem RH / Empregare.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Seção de Vantagens / Cultura */}
          <div className="mt-16 border-t border-border pt-12 sm:mt-24 sm:pt-16">
            <div className="mx-auto max-w-2xl text-center">
              <span className="text-[0.7rem] font-extrabold uppercase tracking-widest text-primary">
                Por que a Contato?
              </span>
              <h2 className="mt-2 text-2xl font-extrabold uppercase tracking-tight text-brand-deep sm:text-3xl">
                Conectando você ao seu futuro
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Trabalhe com autonomia, desenvolvimento contínuo e paixão em uma das maiores operadoras do Sul catarinense.
              </p>
            </div>

            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {perks.map((perk, i) => {
                const Icon = perk.icon;
                return (
                  <div
                    key={i}
                    className="rounded-3xl border border-border bg-card p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-md"
                  >
                    <span className="grid size-12 place-items-center rounded-2xl bg-secondary text-primary">
                      <Icon className="size-6" />
                    </span>
                    <h3 className="mt-4 text-base font-extrabold text-brand-deep">
                      {perk.title}
                    </h3>
                    <p className="mt-2 text-xs leading-relaxed text-muted-foreground sm:text-sm">
                      {perk.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
